import { useEffect, useRef, useCallback } from 'react';
import debounce from '../utils/debounce';
import logger from '../utils/logger';
import { sendReadMarkersWithCompliance } from '../utils/readMarkerUtils';
import { isAppInForeground } from '../utils/appFocus';
import { clearFloorForRoom } from '../utils/unreadFloor';

/**
 * How long after room entry we block the cleanup's marker update so the
 * scroll-to-marker effect has a chance to reposition the view before cleanup
 * measures "at bottom". Mirrors the receipt mount-guard in useReadReceipts.
 */
export const MARKER_SETTLED_TIMEOUT_MS = 2000;

/**
 * Minimum dwell time before the pre-settle at-bottom escape hatch fires.
 *
 * NED-3388 partial-view Scenario 1: when room content fits the viewport,
 * `isAtBottomRef` is trivially `true` (scrollHeight === clientHeight). The
 * IntersectionObserver-based visibility tracker also reports the last event
 * as visible. Without a dwell gate, cleanup on sub-second navigate-away
 * would advance the marker and clear the badge even though the user had no
 * time to read anything.
 *
 * Invariant: MIN_AT_BOTTOM_DWELL_MS <= MARKER_SETTLED_TIMEOUT_MS. Otherwise
 * the escape hatch is dead code (marker-settled path takes over first).
 *
 * Tests that depend on cleanup firing (e.g., read-marker.spec.js "Marker and
 * badge clear after navigating away and back") must dwell > this threshold
 * before navigating away. That is a correctness precondition, not a timing
 * hack — a user needs at least this long in a room to have "read" it.
 */
export const MIN_AT_BOTTOM_DWELL_MS = 1000;

/**
 * Manages the read marker for a room, updating it when the user leaves.
 * When at the bottom, updates to the last regular message.
 * When not at bottom, updates to the last visible event (so unread count reflects only unseen messages).
 * @param {object} params - The parameters for the hook.
 * @param {HTMLElement} params.scrollContainer - The DOM element for the message list.
 * @param {string} params.roomId - The ID of the current room.
 * @param {string} params.readMarkerId - The current read marker event ID.
 * @param {Function} params.setReadMarker - Function to update the read marker.
 * @param {object} params.client - The Matrix client instance.
 * @param {string} params.receiptType - The receipt type from settings ('m.read' or 'm.read.private').
 * @param {Function} params.getLastVisibleEventId - Returns the last visible event ID in the viewport (from useVisibleMessages).
 * @returns {object} An object containing the `isAtBottomRef` and `updateReadMarkerOnSend`.
 */
export const useReadMarker = ({
  scrollContainer,
  roomId,
  readMarkerId,
  setReadMarker,
  client,
  receiptType = 'm.read.private',
  getLastVisibleEventId,
}) => {
  // store the current roomId so we can detect room changes vs initial load
  const prevRoomIdRef = useRef(roomId);

  // Keep client accessible in cleanup (logout guard, live timeline access)
  const clientRef = useRef(client);
  useEffect(() => { clientRef.current = client; }, [client]);

  const isAtBottomRef = useRef(true);
  // Track if scroll position was ever measured (scrollContainer was valid)
  const scrollTrackingInitializedRef = useRef(false);

  // Phase 3: Debouncing refs for batching rapid message sends
  const debounceTimerRef = useRef(null);
  const pendingUpdateRef = useRef(null);

  // Cache the last visible event ID in a ref so cleanup can read it without querying DOM.
  // Updated on scroll events and render cycles via the scroll handler below.
  const lastVisibleEventIdRef = useRef(null);

  // This ref holds the latest data for the cleanup function, preventing stale closures
  // when the component re-renders for a new room. The cleanup reads the live timeline
  // directly from the Matrix client (via clientRef) rather than from props.
  const cleanupDataRef = useRef({});
  useEffect(() => {
    cleanupDataRef.current = { readMarkerId, setReadMarker, roomId };
  }, [roomId, readMarkerId, setReadMarker]);

  // Track scroll position (runs when scrollContainer changes)
  useEffect(() => {
    if (!scrollContainer) return;

    // Mark that we've started tracking scroll position
    scrollTrackingInitializedRef.current = true;

    const handleScroll = () => {
      const { scrollHeight, scrollTop, clientHeight } = scrollContainer;
      const atBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 5;
      isAtBottomRef.current = atBottom;
      // Cache last visible event ID so cleanup can read it without DOM query
      if (getLastVisibleEventId) {
        lastVisibleEventIdRef.current = getLastVisibleEventId();
      }
    };

    const debouncedScrollHandler = debounce(handleScroll, 100);
    scrollContainer.addEventListener('scroll', debouncedScrollHandler);
    handleScroll(); // Initial check

    return () => {
      scrollContainer.removeEventListener('scroll', debouncedScrollHandler);
    };
  }, [scrollContainer, getLastVisibleEventId]);

  // Marker-settled guard: prevent cleanup from marking messages as read during
  // the initial mount period (before scroll-to-marker has repositioned the view).
  // Mirrors the mount guard in useReadReceipts. Without this, the scroll handler's
  // initial check sets isAtBottomRef=true and the cleanup immediately marks the
  // last timeline event as read via sendReadMarkersWithCompliance local echo.
  const markerSettledRef = useRef(false);
  const markerSettledTimerRef = useRef(null);
  // Telemetry only — used by the [DIAG-3388] log below to report how long
  // the user was in this room at cleanup time. Not part of any guard.
  const roomEnterTimeRef = useRef(0);

  useEffect(() => {
    markerSettledRef.current = false;
    // Reset to safe default until scroll handler re-measures for the new room.
    // Old room's cleanup reads isAtBottomRef before this setup runs (React
    // guarantees all cleanups complete before any setups), so this won't
    // affect the leaving room's marker update.
    isAtBottomRef.current = false;
    roomEnterTimeRef.current = Date.now();
    if (markerSettledTimerRef.current) {
      clearTimeout(markerSettledTimerRef.current);
    }
    markerSettledTimerRef.current = setTimeout(() => {
      markerSettledRef.current = true;
      markerSettledTimerRef.current = null;
    }, MARKER_SETTLED_TIMEOUT_MS);

    return () => {
      if (markerSettledTimerRef.current) {
        clearTimeout(markerSettledTimerRef.current);
        markerSettledTimerRef.current = null;
      }
    };
  }, [roomId]);

  // On room change, update marker if needed
  useEffect(() => {
    prevRoomIdRef.current = roomId;

    return () => {
      const { readMarkerId, setReadMarker, roomId: refRoomId } = cleanupDataRef.current;
      const leavingRoomId = roomId; // from closure - the room we're leaving

      // Guard against race condition where ref was updated before cleanup ran
      if (leavingRoomId !== refRoomId) {
        logger.warn('[ReadMarker] Room mismatch in cleanup, skipping', { leavingRoomId, refRoomId });
        return;
      }

      // Don't update the marker if the scroll-to-marker hasn't settled yet,
      // UNLESS the user is at the bottom and scroll tracking was initialized.
      // The mount guard prevents marking all messages as read when the room
      // loads at the bottom before scroll-to-marker repositions the view.
      // However, if scroll tracking is active and the user is genuinely at
      // the bottom (e.g. a room with few messages, or they scrolled down),
      // we should still update the marker — otherwise a quick navigate-away
      // leaves the marker stale and the badge persists on return.

      if (!isAppInForeground()) {
        logger.debug('[ReadMarker] Skipping cleanup - app not in foreground for', leavingRoomId);
        lastVisibleEventIdRef.current = null;
        return;
      }

      if (!markerSettledRef.current) {
        // See MIN_AT_BOTTOM_DWELL_MS for full rationale. When content fits
        // the viewport `isAtBottomRef` is trivially true on mount; without
        // a dwell gate, sub-second nav-aways would advance the marker and
        // clear the badge (NED-3388 partial-view Scenario 1).
        const timeInRoom = Date.now() - roomEnterTimeRef.current;
        if (
          !scrollTrackingInitializedRef.current ||
          !isAtBottomRef.current ||
          timeInRoom < MIN_AT_BOTTOM_DWELL_MS
        ) {
          logger.debug('[ReadMarker] Skipping cleanup - marker not settled yet for', leavingRoomId, { timeInRoom });
          lastVisibleEventIdRef.current = null;
          return;
        }
        logger.debug('[ReadMarker] Marker not settled but user is at bottom and has dwelled, proceeding with cleanup for', leavingRoomId);
      }

      // Read refs at cleanup time — these reflect the CURRENT room's exit state.
      // React guarantees old cleanup runs before new setup, so the refs still hold
      // the leaving room's values (the scroll-tracking effect's listener is still
      // attached and its cleanup hasn't run yet).
      const wasScrollTracking = scrollTrackingInitializedRef.current;
      const wasAtBottom = isAtBottomRef.current;
      const cachedLastVisible = lastVisibleEventIdRef.current;

      // Reset last-visible for the next room. Keep scrollTrackingInitializedRef
      // true — the scroll-tracking effect (deps: [scrollContainer, getLastVisibleEventId])
      // won't re-run on room change because Messages doesn't remount, so if we
      // reset it to false here it would stay permanently false.
      lastVisibleEventIdRef.current = null;

      logger.debug('[ReadMarker] cleanup', {
        leavingRoomId: leavingRoomId?.slice(-8),
        wasAtBottom,
        wasScrollTracking,
        cachedLastVisible: cachedLastVisible?.slice(-12),
      });

      if (leavingRoomId && wasScrollTracking) {
        if (wasAtBottom) {
          // Read from the room's live timeline directly instead of stale props.
          const cleanupClient = clientRef.current;
          const room = cleanupClient?.getRoom?.(leavingRoomId);
          const timeline = room?.getLiveTimeline?.()?.getEvents?.();

          if (!timeline || timeline.length === 0) {
            logger.debug('[ReadMarker] No timeline events available during cleanup for', leavingRoomId);
            // Fall through — no marker update possible
          } else {
            // Positive filter for user-visible message types.
            let lastRegularEvent = null;
            for (let i = timeline.length - 1; i >= 0 && !lastRegularEvent; i--) {
              const evt = timeline[i];
              const type = evt.getType();
              if (type === 'm.room.message' || type === 'm.reaction') {
                lastRegularEvent = evt;
              }
            }

            if (lastRegularEvent && readMarkerId !== lastRegularEvent.getId()) {
              logger.debug(
                `[ReadMarker] Leaving room ${leavingRoomId} at bottom. ` +
                `Updating marker to ${lastRegularEvent.getId()} (from live timeline, ${timeline.length} events)`,
              );
              setReadMarker(lastRegularEvent.getId(), leavingRoomId);
            }
          }
        } else {
          // User NOT at bottom: update marker to last visible event from scroll handler.
          if (cachedLastVisible && readMarkerId !== cachedLastVisible) {
            logger.debug(`[ReadMarker] Leaving room ${leavingRoomId} not at bottom. Updating marker to last visible: ${cachedLastVisible}`);
            setReadMarker(cachedLastVisible, leavingRoomId);
          }
        }
      }
    };
  }, [roomId]);

  // Phase 3: Implement updateReadMarkerOnSend with debouncing for rapid message sends
  const updateReadMarkerOnSend = useCallback((eventId, destinationRoomId) => {
    if (!client || !eventId || !destinationRoomId) {
      logger.warn('[useReadMarker] Missing parameters for updateReadMarkerOnSend', {
        hasClient: !!client,
        hasEventId: !!eventId,
        hasRoomId: !!destinationRoomId,
      });
      return;
    }

    // Store the latest update info (overwrites previous if exists)
    pendingUpdateRef.current = { eventId, destinationRoomId };

    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer (300ms debounce)
    debounceTimerRef.current = setTimeout(async () => {
      // Skip if app lost focus while debounce was pending (covers queued/retry sends)
      if (!isAppInForeground()) {
        logger.debug('[useReadMarker] Skipping marker update on send - app not in foreground');
        pendingUpdateRef.current = null;
        debounceTimerRef.current = null;
        return;
      }

      const update = pendingUpdateRef.current;
      if (!update) return;

      try {
        logger.debug(`[useReadMarker] Updating read marker (debounced): ${update.eventId} in room ${update.destinationRoomId}`);

        // Get the room and event objects for proper API call
        const room = client.getRoom(update.destinationRoomId);
        const event = room?.findEventById(update.eventId);

        if (!event) {
          // m.fully_read only needs the event ID string, not the MatrixEvent object.
          // The event may not be in the local timeline yet (local echo sync lag).
          // Read receipts (m.read, m.read.private) are skipped since they require
          // the event object — they'll catch up on the next receipt send.
          logger.warn('[useReadMarker] Event not found for read marker update, updating m.fully_read only:', update.eventId);
          if (!client?.isLoggedIn?.()) {
            logger.warn('[useReadMarker] Client not logged in during fallback, aborting');
            return;
          }
          try {
            await client.setRoomReadMarkers(update.destinationRoomId, update.eventId);
            logger.debug('[useReadMarker] m.fully_read updated via fallback path');
            // Sending your own message is an unambiguous "I've read this room" signal;
            // clear any persistent unread floor so the badge doesn't get pinned above 0
            // when the normal active-view clear (gated by the mount guard) didn't fire.
            const ownUserId = client.getUserId?.();
            if (ownUserId) clearFloorForRoom(ownUserId, update.destinationRoomId);
          } catch (e) {
            logger.warn('[useReadMarker] Failed to update m.fully_read for unresolved event:', e.message);
          }
          if (update.destinationRoomId === roomId) {
            setReadMarker(update.eventId, update.destinationRoomId);
          }
          return;
        }

        await sendReadMarkersWithCompliance(
          client,
          update.destinationRoomId,
          update.eventId,
          event,
          receiptType,
        );

        logger.debug('[useReadMarker] Successfully updated read marker via API');

        // Sending your own message is an unambiguous "I've read this room" signal;
        // clear any persistent unread floor so the badge doesn't get pinned above 0
        // when the normal active-view clear (gated by the mount guard) didn't fire.
        const ownUserId = client.getUserId?.();
        if (ownUserId) clearFloorForRoom(ownUserId, update.destinationRoomId);

        // Update local state only if it's the current room
        if (update.destinationRoomId === roomId) {
          logger.debug('[useReadMarker] Updating local state for current room');
          setReadMarker(update.eventId, update.destinationRoomId);
        }
      } catch (error) {
        // Enhanced error logging with full context
        logger.error('[useReadMarker] Failed to update read marker on send:', {
          eventId: update.eventId,
          roomId: update.destinationRoomId,
          currentRoomId: roomId,
          errorMessage: error.message,
          errorCode: error.errcode || 'unknown',
          errorName: error.name,
        });

        // FALLBACK: Update local state even if API fails
        // This ensures UI shows message as read even if server sync fails
        // The next Matrix sync will eventually reconcile state
        if (update.destinationRoomId === roomId) {
          logger.debug('[useReadMarker] Applying fallback: updating local state despite API failure');
          try {
            setReadMarker(update.eventId, update.destinationRoomId);
          } catch (localError) {
            logger.error('[useReadMarker] Fallback also failed:', localError);
            // This is a critical error but don't throw - graceful degradation
          }
        }
      } finally {
        // Always cleanup refs regardless of success/failure
        pendingUpdateRef.current = null;
        debounceTimerRef.current = null;
      }
    }, 300);
  }, [client, roomId, setReadMarker, receiptType]);

  // Phase 3: Cleanup effect - cancel pending timers on unmount or room change
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      pendingUpdateRef.current = null;
    };
  }, [roomId]);

  return {
    isAtBottomRef,
    updateReadMarkerOnSend,
  };
};
